import React, { useState, useEffect } from "react";
import { FaSpinner, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { PiPlusBold } from "react-icons/pi";
import { toast } from "react-toastify";

import { fetchAutoMessage, setAutoEmail } from "../../../redux/slices/ceoSlice";

export default function ModalAutoEmailMessage() {
  const [showModal, setShowModal] = useState(false);
  const status = useSelector((state) => state.ceo?.autoEmailStatus);
  const autoMessage = useSelector((state) => state.ceo?.autoMessage);

  const dispatch = useDispatch();

  const initialFormData = {
    Day1: { subject: "", message: "" },
    Day3: { subject: "", message: "" },
    Day7: { subject: "", message: "" }
  };

  const [formData, setFormData] = useState({ ...initialFormData });
  const [activeDay, setActiveDay] = useState("Day1");

  // Pre-fill form with existing data when modal opens and autoMessage is available
  useEffect(() => {
    if (showModal && autoMessage?.data) {
      setFormData({
        Day1: {
          subject: autoMessage.data.Day1?.subject || "",
          message: autoMessage.data.Day1?.message || ""
        },
        Day3: {
          subject: autoMessage.data.Day3?.subject || "",
          message: autoMessage.data.Day3?.message || ""
        },
        Day7: {
          subject: autoMessage.data.Day7?.subject || "",
          message: autoMessage.data.Day7?.message || ""
        }
      });
    }
  }, [showModal, autoMessage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [activeDay]: {
        ...prevFormData[activeDay],
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert empty strings to null and send all days
    const dataToSend = {
      Day1: {
        subject: formData.Day1.subject || null,
        message: formData.Day1.message || null
      },
      Day3: {
        subject: formData.Day3.subject || null,
        message: formData.Day3.message || null
      },
      Day7: {
        subject: formData.Day7.subject || null,
        message: formData.Day7.message || null
      }
    };

    // Wrap in "body" payload
    const payload = {
      body: dataToSend
    };

    try {
      const res = await dispatch(setAutoEmail(payload));

      setFormData({ ...initialFormData });
      setActiveDay("Day1");

      await dispatch(fetchAutoMessage());
      toast.success(res.payload.message);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save auto email messages");
    }
    setShowModal(false);
  };

  const DayButton = ({ day, label }) => (
    <button
      type="button"
      onClick={() => setActiveDay(day)}
      className={`px-4 py-2 rounded-lg font-medium ${
        activeDay === day
          ? "bg-blue-600 text-white"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="flex gap-[8px] bg-[#1F7BF4] text-white items-center py-2 px-4 text-[14px] hover:shadow-lg rounded-[8px] font-medium"
      >
        <PiPlusBold className=" text-lg font-extrabold" />
        {autoMessage?.data ? "Edit Auto Email Message" : "Auto Email Message"}
      </button>
      {showModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-5xl">
              <div className="border-0 rounded-lg p-6 shadow-lg relative flex flex-col bg-white outline-none focus:outline-none">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center"
                >
                  <FaTimes className="text-gray-300 text-xl" />
                  <span className="sr-only">Close modal</span>
                </button>
                <div className=" text-center">
                  <h1 className="text-2xl text-gray-800 text-center font-medium mb-4">
                    {autoMessage?.data ? "Edit" : "Create"} Auto Email Message
                  </h1>
                </div>
                <div className="flex flex-col items-center px-8 py-4 gap-6">
                  
                  {/* Day Selection Tabs */}
                  <div className="flex gap-2">
                    <DayButton day="Day1" label="Day 1" />
                    <DayButton day="Day3" label="Day 3" />
                    <DayButton day="Day7" label="Day 7" />
                  </div>

                  {/* Content for Active Day */}
                  <div className="flex gap-4 w-full">
                    <div className="flex-1">
                      <label
                        htmlFor="subject"
                        className="text-[14px] text-center text-[#4D5464] font-medium tracking-[0.07px] mb-2 block"
                      >
                        Subject for {activeDay}
                      </label>
                      <textarea
                        name="subject"
                        id="subject"
                        cols="30"
                        rows="20"
                        value={formData[activeDay].subject}
                        onChange={handleInputChange}
                        placeholder="Type subject here..."
                        className="p-2 rounded-lg text-[14px] bg-[#F9F9FC] w-full border-2 border-[#E0E2E7] h-36"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="message"
                        className="text-[14px] text-center text-[#4D5464] font-medium tracking-[0.07px] mb-2 block"
                      >
                        Message for {activeDay}
                      </label>
                      <textarea
                        name="message"
                        id="message"
                        cols="30"
                        rows="20"
                        value={formData[activeDay].message}
                        onChange={handleInputChange}
                        placeholder="Type message here..."
                        className="p-2 rounded-lg text-[14px] bg-[#F9F9FC] w-full border-2 border-[#E0E2E7] h-36"
                      />
                    </div>
                  </div>

                  <div className="flex mt-2 gap-4 justify-center">
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-sm text-blue-500 font-semibold bg-blue-100 py-2 px-4 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      className="flex gap-1 rounded-lg items-center bg-blue-600 text-white py-2 px-4"
                      disabled={status === "loading"}
                      onClick={handleSubmit}
                    >
                      {status === "loading" ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          <p className="text-sm font-semibold whitespace-nowrap">
                            Saving ...
                          </p>
                        </>
                      ) : (
                        <>
                          <PiPlusBold className="w-4 h-4" />
                          <p className="text-sm font-semibold whitespace-nowrap">
                            {autoMessage?.data ? "Update" : "Save"} Auto Email Messages
                          </p>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
}